/**
 * Home address rules for My Home (items 4 and 5, 2026-10-02).
 *
 * Warren: "Postal code: text input accepting Canadian postal codes and US
 * ZIPs, validated by country." A home is in the United States or Canada,
 * and the country decides what the region and the postal code may be.
 * The web form and the Api run these same rules, so the form can refuse
 * what the server would refuse before anything is sent.
 *
 *   US ZIP            12345 or 12345-6789 (nine digits without the dash
 *                     are accepted and stored dashed).
 *   Canadian postal   A1A 1A1, stored uppercase with one space. D F I O Q U
 *                     never appear, and W and Z never lead.
 *   State / province  by code or by name, case-insensitive, accents folded
 *                     (Quebec and Québec both resolve to QC), with or
 *                     without periods (B.C., D.C.), and by the common
 *                     short forms people type (PEI, Nfld., Ont., NWT,
 *                     Washington DC).
 *
 * An explicit country that disagrees with the state is an error, never a
 * silent flip. With no country (a stale client), the state decides, the
 * way geoNormalize's consistency rule always has. A Canadian postal code
 * whose first letter belongs to another province is only a soft warning:
 * the letter map is a strong hint, not a rule the post office promises.
 *
 * Stored forms are geoNormalize's canonical full names ("United States",
 * "Canada", "Ontario"), which these lists share.
 *
 * Pure, no I/O. Messages are English for the Api; the web maps `code` to
 * its own translated copy.
 */
import { US_STATES, CA_PROVINCES, CA_POSTAL_LETTER_TO_PROVINCE, } from '../types/incentives.js';
export const HOME_COUNTRIES = ['US', 'CA'];
/** The stored country names (geoNormalize's full-name form). */
export const HOME_COUNTRY_NAMES = {
    US: 'United States',
    CA: 'Canada',
};
/** Input maxLength per country: 12345-6789 is 10, A1A 1A1 is 7. */
export const HOME_POSTAL_MAX_LENGTH = { US: 10, CA: 7 };
/** The states or provinces a home in `country` may name. */
export function homeRegionsFor(country) {
    return country === 'CA' ? CA_PROVINCES : US_STATES;
}
/** Uppercase, accents folded, whitespace collapsed: the comparison form. */
function fold(value) {
    return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .replace(/\s+/g, ' ')
        .toUpperCase();
}
const COUNTRY_ALIASES = {
    US: 'US', USA: 'US', 'U.S.': 'US', 'U.S.A.': 'US',
    'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US',
    CA: 'CA', CAN: 'CA', CANADA: 'CA',
};
/**
 * 'US' | 'CA' from a code or a stored full name, or null for anything else
 * (absent, another country). Note 'CA' here is Canada: California only
 * ever appears in the state field.
 */
export function homeCountryFromAny(value) {
    if (!value)
        return null;
    return COUNTRY_ALIASES[fold(value)] ?? null;
}
/**
 * Short forms people type for a region, keyed in the comparison form with
 * periods dropped and commas read as spaces: the traditional Canadian
 * abbreviations (Alta., Man., Nfld., Ont., Que., Sask., Nvt., P.E.I.,
 * N.W.T.), PQ (Quebec's old postal code), the names without their second
 * half, and Washington DC. None of them is a code or a name in either
 * list, so none can shadow one.
 */
const REGION_ALIASES = {
    // Canada
    ALTA: 'AB', MAN: 'MB', NFLD: 'NL', NEWFOUNDLAND: 'NL', LABRADOR: 'NL',
    'NEWFOUNDLAND LABRADOR': 'NL', 'NEWFOUNDLAND & LABRADOR': 'NL',
    ONT: 'ON', QUE: 'QC', PQ: 'QC', SASK: 'SK', PEI: 'PE', NVT: 'NU', NWT: 'NT',
    'YUKON TERRITORY': 'YT',
    // United States
    'WASHINGTON DC': 'DC', 'WASHINGTON D C': 'DC',
};
/**
 * Resolve a typed state or province by code, name or common short form.
 * With a country, only that country's list is searched; without one, both
 * are (the US and Canadian code sets do not collide, and neither do the
 * names or the short forms).
 */
export function resolveHomeRegion(state, country) {
    if (!state)
        return null;
    const q = fold(state);
    if (!q)
        return null;
    // "B.C." and "D.C." read as their codes; "Nfld." as its short form.
    const bare = q.replace(/\./g, '').replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
    const squeezed = bare.replace(/ /g, '');
    const alias = REGION_ALIASES[bare];
    const countries = country ? [country] : ['US', 'CA'];
    for (const c of countries) {
        const hit = homeRegionsFor(c).find((r) => r.code === q || fold(r.name) === q
            || (squeezed.length === 2 && r.code === squeezed) || r.code === alias);
        if (hit)
            return { country: c, code: hit.code, name: hit.name };
    }
    return null;
}
const US_ZIP_RE = /^(\d{5})(?:[\s-]?(\d{4}))?$/;
// First letter: no D F I O Q U W Z. Third and fifth: no D F I O Q U.
const CA_POSTAL_RE = /^([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z])[\s-]?(\d[ABCEGHJ-NPRSTV-Z]\d)$/;
/**
 * The stored form of a postal code for `country`, or null when it is not
 * one: '12345', '12345-6789', 'K1A 0B1'.
 */
export function normalizePostalCode(country, raw) {
    if (!raw)
        return null;
    const v = raw.trim().toUpperCase();
    if (country === 'US') {
        const m = US_ZIP_RE.exec(v);
        if (!m)
            return null;
        return m[2] ? `${m[1]}-${m[2]}` : m[1];
    }
    const m = CA_POSTAL_RE.exec(v);
    return m ? `${m[1]} ${m[2]}` : null;
}
const REGION_NOUN = { US: 'US state', CA: 'Canadian province' };
/**
 * The region half of the rule. With a country: the state must be one of
 * that country's, by code or name, and a region of the OTHER country is
 * named as such. Without one: the state must name a US state or a
 * Canadian province, and that decides the country.
 */
export function checkHomeRegion(country, state) {
    const typed = (state ?? '').trim();
    if (!country) {
        const any = resolveHomeRegion(typed);
        if (any)
            return { ok: true, region: any };
        return {
            ok: false, field: 'state', code: 'STATE_OR_PROVINCE_REQUIRED',
            message: 'Enter a US state or a Canadian province.',
        };
    }
    if (!typed) {
        return {
            ok: false, field: 'state', code: 'STATE_REQUIRED',
            message: country === 'CA' ? 'Enter your province.' : 'Enter your state.',
        };
    }
    const region = resolveHomeRegion(typed, country);
    if (region)
        return { ok: true, region };
    const other = resolveHomeRegion(typed, country === 'US' ? 'CA' : 'US');
    if (other) {
        return {
            ok: false, field: 'state', code: 'STATE_WRONG_COUNTRY',
            message: `${other.name} is a ${REGION_NOUN[other.country]}, not a ${REGION_NOUN[country]}.`,
        };
    }
    return {
        ok: false, field: 'state', code: 'STATE_UNKNOWN',
        message: country === 'CA'
            ? 'Enter a Canadian province, like ON or Ontario.'
            : 'Enter a US state, like CA or California.',
    };
}
/** The postal half of the rule, for a known country. */
export function checkHomePostal(country, zip) {
    if (!(zip ?? '').trim()) {
        return {
            ok: false, field: 'zip', code: 'ZIP_REQUIRED',
            message: country === 'CA' ? 'Enter your postal code.' : 'Enter your ZIP code.',
        };
    }
    const normalized = normalizePostalCode(country, zip);
    if (normalized)
        return { ok: true, zip: normalized };
    return country === 'CA'
        ? { ok: false, field: 'zip', code: 'POSTAL_INVALID', message: 'Enter a postal code like K1A 0B1.' }
        : { ok: false, field: 'zip', code: 'ZIP_INVALID', message: 'Enter a 5-digit ZIP code, like 90001.' };
}
/**
 * Soft check: the province codes a Canadian postal code's first letter
 * usually belongs to, when `regionCode` is not one of them. X is shared
 * by the Northwest Territories and Nunavut. Null when there is nothing
 * to say (a match, a US home, or a code too short to read).
 */
export function postalProvinceMismatch(country, postal, regionCode) {
    if (country !== 'CA' || !regionCode || !postal)
        return null;
    const letter = postal.trim().charAt(0).toUpperCase();
    const primary = CA_POSTAL_LETTER_TO_PROVINCE[letter];
    if (!primary)
        return null;
    const expected = letter === 'X' ? ['NT', 'NU'] : [primary];
    return expected.includes(regionCode.toUpperCase()) ? null : { expected };
}
/**
 * The whole rule: region first (it may decide the country), then the
 * postal code for the resolved country. The first failure is returned,
 * naming its field.
 */
export function validateHomeAddress(input) {
    const region = checkHomeRegion(input.country, input.state);
    if (!region.ok)
        return region;
    const country = region.region.country;
    const postal = checkHomePostal(country, input.zip);
    if (!postal.ok)
        return postal;
    return {
        ok: true,
        country,
        countryName: HOME_COUNTRY_NAMES[country],
        regionCode: region.region.code,
        regionName: region.region.name,
        zip: postal.zip,
        postalWarning: postalProvinceMismatch(country, postal.zip, region.region.code),
    };
}
//# sourceMappingURL=homeAddress.js.map